import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import QueryParamsContext from '@/contexts/query-params/QueryParamsContext';
import { deserializeValue, serializeValue } from '@/utils/filterUtils';

/**
 * URL-persisted QueryParamsProvider with a namespace prefix.
 * Multiple instances on the same page use different prefixes
 * (e.g., "m_" for members, "t_" for tasks) so they don't conflict.
 */
export default function LocalQueryParamsProvider({
  children,
  defaultParams = {},
  prefix = '',
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const queryParams = useMemo(() => {
    const result = { ...defaultParams };
    for (const [key, rawValue] of searchParams.entries()) {
      if (prefix && !key.startsWith(prefix)) continue;
      const cleanKey = prefix ? key.slice(prefix.length) : key;
      if (cleanKey in defaultParams || !prefix) {
        result[cleanKey] = deserializeValue(rawValue);
      }
    }
    return result;
  }, [searchParams, defaultParams, prefix]);

  const setQueryParams = useCallback(
    (newParams) => {
      setSearchParams((prev) => {
        const updated = new URLSearchParams(prev);

        Object.entries(newParams).forEach(([key, value]) => {
          const prefixedKey = `${prefix}${key}`;
          if (value === undefined || value === null) {
            updated.delete(prefixedKey);
          } else {
            updated.set(prefixedKey, serializeValue(value));
          }
        });

        return updated;
      });
    },
    [prefix, setSearchParams]
  );

  const queryParamsString = useMemo(() => {
    const filtered = {};
    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== undefined) filtered[key] = value;
    }
    return new URLSearchParams(filtered).toString();
  }, [queryParams]);

  const value = useMemo(
    () => ({ queryParams, queryParamsString, setQueryParams }),
    [queryParams, queryParamsString, setQueryParams]
  );

  return (
    <QueryParamsContext.Provider value={value}>
      {children}
    </QueryParamsContext.Provider>
  );
}
